import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import axios from 'axios';
import { deconnexionEnCours } from '../../utils/logoutBroadcast';

const client = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

export const useAxios = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;

  useEffect(() => {
    const requestInterceptor = client.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    return () => {
        client.interceptors.request.eject(requestInterceptor);
    };
  }, [token]);

  // Intercepteur de réponse : sur un 401, on vérifie qu'on est encore connecté
  // via un renouvellement silencieux OIDC. Succès → on rejoue la requête d'origine.
  // Échec → redirection vers /login. Les visiteurs non connectés (page visite
  // publique) ne sont jamais redirigés.
  useEffect(() => {
    const redirectToLogin = () => {
      // Évite les boucles de redirection si on est déjà sur /login
      if (window.location.pathname.startsWith('/login')) return;
      // Nettoyage de la session locale (cf. userActions.logout / LoginScreen.cleanAuth)
      try {
        auth.removeUser();
      } catch (e) { /* no-op */ }
      localStorage.removeItem('userInfo');
      localStorage.removeItem('accessToken');
      window.location.href = '/login';
    };

    const onRejected = async (error) => {
      const status = error.response?.status;
      const config = error.config;

      // On ne traite que les 401 (on laisse passer 403, 500, etc.)
      if (status !== 401 || !config) {
        return Promise.reject(error);
      }

      // Garde session : aucun utilisateur connecté (ex. page visite publique)
      // → pas de redirection, on propage l'erreur.
      if (!auth.user) {
        return Promise.reject(error);
      }

      // Garde déconnexion : une déconnexion est en cours (ici ou dans un autre onglet).
      // Un renew silencieux trouverait le cookie SSO encore vivant et recréerait une
      // session que la déconnexion en cours ne terminera pas (cf. utils/logoutBroadcast.js).
      if (deconnexionEnCours()) {
        return Promise.reject(error);
      }

      // Garde anti-boucle : on a déjà tenté un renew sans succès → déconnexion.
      if (config._retry) {
        redirectToLogin();
        return Promise.reject(error);
      }

      config._retry = true;
      try {
        const user = await auth.signinSilent();
        if (user?.access_token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${user.access_token}`;
          // Rejoue via l'instance axios globale : le config complet fonctionne
          // aussi bien pour le client partagé que pour les appels axios globaux.
          return axios(config);
        }
        throw new Error('no user after silent renew');
      } catch (e) {
        redirectToLogin();
        return Promise.reject(error);
      }
    };

    const clientResponseInterceptor = client.interceptors.response.use(
      (response) => response,
      onRejected
    );
    const globalResponseInterceptor = axios.interceptors.response.use(
      (response) => response,
      onRejected
    );

    return () => {
      client.interceptors.response.eject(clientResponseInterceptor);
      axios.interceptors.response.eject(globalResponseInterceptor);
    };
  }, [auth]);

  return client;
};
